import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { AssetStyleId } from 'fym-shared/src/music24/assetStyles';
import { isKitCardAssetType } from 'fym-shared/src/music24/story/kitCard';
import { TypedMemo } from '../../../../common/types';
import { Panel } from '../../../components/primitives/Panel';
import { useOpenSongPage } from '../../../../smartLinks/catalog/song/page/useOpenSongPage';
import { useThemeStillSong } from '../../releases/theme/useThemeStillSong';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import { studioCredits } from '../studioRules';
import { studioMessages, studioMomentMessages } from '../studioMessages';
import { StudioThemeDialog } from '../StudioThemeDialog';
import { StoryPreview } from '../frames/StoryPreview';
import { CardPreview } from '../frames/CardPreview';
import { studioCards } from '../frames/studioCard';
import { studioStoryStyle } from '../frames/studioStory';
import type { DetailsStepProps } from './detailsStepProps';
import { ImageDetails } from './ImageDetails';
import { ImagePreview } from './imageTiles/ImagePreview';
import { ImageStyleTiles } from './imageTiles/ImageStyleTiles';
import { FromYourTheme } from './imageTiles/FromYourTheme';
import { imageTileMessages } from './imageTiles/imageTileMessages';
import { isStoryStackType, storyOfMoment } from './imageTiles/storyPreviewContext';
import { useStoryPreviewContext } from './imageTiles/useStoryPreviewContext';
import { StudioRail } from './StudioRail';
import { RailNotice } from './RailNotice';
import { imageHasOptions } from './imageOptions';
import { WideCardPreviews } from './WideCardPreviews';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Step 3 for an image (design studio-details a1 to a5): the style tiles, the moment tiles and the
 * lyric line panel (`ImageDetails`), the read-only "From your theme" line with "Change theme", and
 * the rail with the large live preview. A Story and a feed post preview is the real Story layout
 * (`StoryPreview`, 300 px, 220 px on a phone; a feed post is the Story stack in its 4:5 or square
 * frame, design `asset-redesign/e Feed post - new`). An image has no Instructions (owner decision 2026-10-08): the theme
 * decides the colours, the fonts and the texture.
 *
 * An image with no choice (the banners, the thumbnail, the link preview, the cover sizes) has no
 * two columns (owner wish 2026-10-08): the "From your theme" line on top, then the rail at the full
 * width with the previews side by side and the summary next to them. A kit card has no Style row:
 * code lays it out in the frame of the theme and the render ignores the style.
 */
const ImageDetailsStepComponent: React.FC<DetailsStepProps> = (props) => {
  const { accountId, item, themeId, details, song, onChange, items, state, isOwner, isMaking, isLookSaving } = props;
  const { songTheme, style, onOpenSongLyrics, onMake, actionFeedback } = props;
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const stillSong = useThemeStillSong(item);
  const context = useStoryPreviewContext(item, song, songTheme, stillSong.artworkUrl);
  const openSongPage = useOpenSongPage();
  const [themeOpen, setThemeOpen] = useState(false);
  const openTheme = useCallback(() => setThemeOpen(true), []);
  const closeTheme = useCallback(() => setThemeOpen(false), []);
  const addLyrics = useCallback(() => onOpenSongLyrics('text'), [onOpenSongLyrics]);
  const changeDate = useCallback(() => openSongPage(item.uuid, 'details'), [item.uuid, openSongPage]);
  const changeStyle = useCallback((next: AssetStyleId) => onChange({ style: next }), [onChange]);
  const credits = useMemo(() => studioCredits(themeId, items), [items, themeId]);
  const kicker =
    details.moment === undefined ? stillSong.kicker : intl.formatMessage(studioMomentMessages[details.moment]);
  const songFacts = useMemo(() => ({ ...stillSong, kicker }), [kicker, stillSong]);
  const story = useMemo(
    () =>
      isStoryStackType(themeId)
        ? storyOfMoment(context, details.moment, details.lyricLine, themeId, details.shape)
        : null,
    [context, details.lyricLine, details.moment, details.shape, themeId]
  );

  let blockedReason: string | null = null;
  if (state === 'open' && isLookSaving) {
    blockedReason = intl.formatMessage(studioMessages.blockedSavingTheme);
  } else if (state === 'open' && items === null) {
    blockedReason = intl.formatMessage(studioMessages.blockedFields);
  }

  const cards = useMemo(() => (story === null ? studioCards(themeId, context) : null), [context, story, themeId]);

  const width = narrow ? 220 : 300;
  const cardPreview =
    cards === null ? null : (
      <div className={styles.cardStack}>
        {cards.map((card) => (
          <CardPreview
            key={card.kind === 'banner' ? card.site : card.kind}
            card={card}
            look={songTheme.look}
            palette={songTheme.palette}
            artworkUrl={context.artworkUrl}
            watermark={context.watermark}
            width={width}
          />
        ))}
      </div>
    );
  const preview =
    cardPreview ??
    (story !== null ? (
      <StoryPreview
        story={story}
        look={songTheme.look}
        palette={songTheme.palette}
        storyStyle={studioStoryStyle(style?.style)}
        artworkUrl={context.artworkUrl}
        logoUrl={context.logoUrl}
        watermark={context.watermark}
        width={width}
      />
    ) : (
      <ImagePreview assetTypeId={themeId} style={style?.style ?? null} context={context} song={songFacts} />
    ));
  const previewNote =
    story === null && cards === null
      ? undefined
      : intl.formatMessage(
          details.moment === 'lyricLine' || themeId === 'lyricCard'
            ? imageTileMessages.honestNoteLyric
            : imageTileMessages.honestNote
        );

  const fromYourTheme = (
    <FromYourTheme
      look={songTheme.look}
      palette={songTheme.palette}
      onChangeTheme={songTheme.kit === undefined ? undefined : openTheme}
    />
  );
  const themeDialog = songTheme.kit !== undefined && (
    <StudioThemeDialog isOpen={themeOpen} accountId={accountId} item={item} kit={songTheme.kit} onClose={closeTheme} />
  );
  const rail = (wide: boolean) => (
    <StudioRail
      accountId={accountId}
      themeId={themeId}
      details={details}
      releaseThemeId={songTheme.look.themeId}
      style={isKitCardAssetType(themeId) ? null : style}
      credits={credits}
      canMake={items !== null && state === 'open' && !isMaking && !isLookSaving}
      isMaking={isMaking}
      onMake={onMake}
      preview={
        wide && cards !== null ? (
          <WideCardPreviews
            cards={cards}
            look={songTheme.look}
            palette={songTheme.palette}
            artworkUrl={context.artworkUrl}
            watermark={context.watermark}
          />
        ) : (
          preview
        )
      }
      previewNote={previewNote}
      notice={<RailNotice themeId={themeId} state={state} isOwner={isOwner} />}
      blockedReason={blockedReason}
      actionFeedback={actionFeedback}
      wide={wide}
    />
  );

  if (!imageHasOptions(themeId, style)) {
    return (
      <div className={styles.detailsWide} data-testid="studio-details" data-layout="wide">
        <Panel>{fromYourTheme}</Panel>
        {themeDialog}
        {rail(true)}
      </div>
    );
  }

  return (
    <div className={clsx(styles.details, styles.detailsImage)} data-testid="studio-details" data-layout="columns">
      <div className={styles.detailsMain}>
        <div className={styles.narrowPreview}>{preview}</div>
        <Panel>
          {style !== null && !isKitCardAssetType(themeId) && (
            <ImageStyleTiles
              assetTypeId={themeId}
              context={context}
              song={songFacts}
              value={style.style}
              moment={details.moment}
              lyricLine={details.lyricLine}
              shape={details.shape}
              onChange={changeStyle}
            />
          )}
          <ImageDetails
            themeId={themeId}
            details={details}
            song={song}
            onChange={onChange}
            context={context}
            style={style?.style ?? null}
            onAddLyrics={addLyrics}
            onChangeDate={changeDate}
          />
          {fromYourTheme}
        </Panel>
        {themeDialog}
      </div>
      {rail(false)}
    </div>
  );
};

export const ImageDetailsStep = TypedMemo(ImageDetailsStepComponent);
